import { ChatMessageType } from "../contracts";
import { isRecord } from "../utils/objectValueConversion";
import type { ChatMessageViewModel } from "./chatMessageViewModel";

export type ToolRequest = {
    type: "tool_use";
    name: string;
    id: string;
};

export type CommercialSimulatorToolName = "commercial_simulator_sku";
export type CommercialSimulatorSubBrandToolName = "commercial_simulator_sub_brand";

export type CommercialSimulatorOptions = {
    sku_id: number;
    sku_name: string;
    retailer_id: number;
    retailer_name: string;
};

export type ProductSelection = {
    id: number;
    name: string;
    retailer_id: number;
    retailer_name: string;
};

export type CommercialSimulatorSubBrandOptions = {
    products: ProductSelection[];
};

export type CommercialSimulatorToolInputRetailer = {
    retailer_name?: string;
};

export type CommercialSimulatorToolInput = {
    sku_name?: string;
    retailers?: CommercialSimulatorToolInputRetailer[];
    objective?: string;
    plan_budget?: string;
    plan_time_period_info?: string;
};

export type SubBrandInfo = {
    base_name: string;
    quantity_1: number;
    quantity_2: number;
    unit_of_measurement_1: number;
    unit_of_measurement_2: number;
};

export type CommercialSimulatorSubBrandToolInput = {
    objective?: string;
    plan_budget?: string;
    plan_requirements?: string[];
    plan_time_period_info?: string;
    retailers?: CommercialSimulatorToolInputRetailer[];
    sub_brand_name?: SubBrandInfo;
};

export type CommercialSimulatorToolRequest = ToolRequest & {
    name: CommercialSimulatorToolName;
    input?: CommercialSimulatorToolInput;
};

export type CommercialSimulatorSubBrandToolRequest = ToolRequest & {
    name: CommercialSimulatorSubBrandToolName;
    input?: CommercialSimulatorSubBrandToolInput;
};

export const isCommercialSimulatorSubBrandToolRequest = (
    payload: unknown
): payload is CommercialSimulatorSubBrandToolRequest => {
    if (!isRecord(payload)) {
        return false;
    }

    return (
        payload.type === "tool_use"
        && typeof payload.id === "string"
        && payload.name === "commercial_simulator_sub_brand"
    );
};

export const isCommercialSimulatorToolRequest = (
    payload: unknown
): payload is CommercialSimulatorToolRequest => {
    if (!isRecord(payload)) {
        return false;
    }

    return (
        payload.type === "tool_use"
        && typeof payload.id === "string"
        && payload.name === "commercial_simulator_sku"
    );
};

export const isToolRequest = (type: ChatMessageType | null | undefined, payload: unknown | null | undefined): payload is ToolRequest => {
    if (!isRecord(payload) || type !== ChatMessageType.ToolRequest) {
        return false;
    }

    return (
        payload.type === "tool_use"
        && typeof payload.id === "string"
    );
};

export const getToolRequestMessageById = <TToolResponse extends ToolRequest = ToolRequest>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): { message: ChatMessageViewModel; payload: TToolResponse } | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        const payload = message.payload;

        if (isToolRequest(message.type, payload) && payload.id === toolRequestId) {
            return { message, payload: payload as TToolResponse };
        }
    }

    return undefined;
};

export const getToolRequestById = <TToolResponse extends ToolRequest = ToolRequest>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): TToolResponse | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        const payload = message.payload;

        if (isToolRequest(message.type, payload) && payload.id === toolRequestId) {
            return payload as TToolResponse;
        }
    }

    return undefined;
};

export const getLastToolRequest = (
    messages: ChatMessageViewModel[] | undefined
): ChatMessageViewModel | undefined => {
    if (!messages) {
        return undefined;
    }

    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];

        if (message.type === ChatMessageType.ToolRequest) {
            return message;
        }
    }

    return undefined;
};

export const isCurrentMessageToolRequest = (
    messages: ChatMessageViewModel[] | undefined
): boolean => {
    const lastMessage = [...(messages ?? [])]
        .sort((a, b) => (b.sequence ?? 0) - (a.sequence ?? 0))
        .at(0);
    return lastMessage?.type === ChatMessageType.ToolRequest;
};
